import classNames from 'classnames';

import { Skeleton, Text } from '@revelio/ui';

import styles from './entity-info-skeleton.module.css';

/**
 * A detail line's placeholder. The `&nbsp;` gives it the exact line box of the
 * `xs` text it stands in for — so the skeleton's rows measure the same as the
 * rows that replace them — and the bar is centred over that box rather than
 * setting the height itself.
 */
const PlaceholderLine = ({ className }: { className?: string }) => (
  <Text
    fontSize="x-sm"
    className={classNames(styles.placeholderLine, className)}
  >
    &nbsp;
    <Skeleton className={styles.placeholderBar} />
  </Text>
);

type EntityInfoSkeletonProps = {
  nodeName: string;
  /** Whether the loaded card will carry the "View Competitors" line. */
  showCompetitorsRow: boolean;
};

/**
 * Loading state for the company tooltip: `EntityInfoContent`'s own rows with the
 * fetched lines as bars, each mirroring the real row's wrapper and typography so
 * the tooltip opens at the size it will settle at. The title is the real name —
 * the node already carries it — so only the fetched lines resolve in place.
 */
export const EntityInfoSkeleton = ({
  nodeName,
  showCompetitorsRow,
}: EntityInfoSkeletonProps) => (
  <div data-testid="entity-info-loading">
    <div className={styles.titleRow}>
      <Text fontSize="sm" weight="semibold" className={styles.title}>
        {nodeName}
      </Text>
    </div>

    {/* Industry */}
    <div className={styles.subtitleRow}>
      <PlaceholderLine className={styles.industryLine} />
    </div>

    {/* Location */}
    <div className={styles.titleRow}>
      <PlaceholderLine className={styles.locationLine} />
    </div>

    {showCompetitorsRow && (
      <div className={styles.detailRow}>
        <PlaceholderLine className={styles.competitorsLine} />
      </div>
    )}
  </div>
);
